import React, { FC, ReactNode } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import xss from "xss"

import { PrefooterFragmentFragment } from "@graphqlTypes"
import { convertStringToKebabCase, getImgixImageData, oneLineHelpScout } from "@utils"

import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"
import HubspotForm from "@components/HubspotForm/HubspotForm"

import "./Prefooter.scss"

type PrefooterProps = {
  bgImgLeftPath?: string, // Override used in the DefaultPrefooter component
  bgImgRightPath?: string, // Override used in the DefaultPrefooter component
  bgImgWidth?: number, // Override used in the DefaultPrefooter component
  children?: ReactNode,
}

const Prefooter: FC<PrefooterFragmentFragment & PrefooterProps> = ({
  bodyText,
  bgImgLeftPath,
  bgImgRightPath,
  bgImgWidth,
  button1Label,
  button1Url,
  button2Label,
  button2Url,
  children,
  hubspotFormData,
  formRedirectUrl,
  title,
}) => {
  return (
    <div className="Prefooter">
      <GatsbyImage
        alt=""
        className="bg-left"
        image={getImgixImageData(
          bgImgLeftPath || "/images/components/Prefooter/Prefooter--bg-left.png",
          bgImgWidth || 616,
          3 / 4
        )}
        role="presentation"
      />
      <GatsbyImage
        alt=""
        className="bg-right"
        image={getImgixImageData(
          bgImgRightPath || "/images/components/Prefooter/Prefooter--bg-right.png",
          bgImgWidth || 616,
          3 / 4
        )}
        role="presentation"
      />
      <div className="container">
        <div className="grid">
          <div className="content column column--2-12-M">
            {title && (
              <h2
                className="type--headline-medium-5XL"
                dangerouslySetInnerHTML={{ __html: xss(oneLineHelpScout(title)) }}
                id={convertStringToKebabCase(title)}
              />
            )}
            {bodyText?.bodyText && <p className="type--text-L max-width--column--6" dangerouslySetInnerHTML={{ __html: xss(bodyText?.bodyText) }} />}
            {!hubspotFormData && (
              <div className="Prefooter--Buttons">
                {
                  /*
                    If formId is unpopulated and formRedirectUrl is populated,
                    assume the intention is a CTA to an ungated PDF (ebook) asset. 
                    An example of this case is seen in CMS Posts with prefooter="Download"
                  */
                }
                {((button1Label && button1Url) || formRedirectUrl) && (
                  <Button
                    href={button1Url || formRedirectUrl || "/"}
                    size={BUTTON_SIZES.L}
                  >
                    {button1Label || "Download the ebook"}
                  </Button>
                )}
                {button2Label && button2Url && (
                  <Button
                    href={button2Url}
                    size={BUTTON_SIZES.L}
                    variant={BUTTON_VARIANTS.SECONDARY}
                  >
                    {button2Label}
                  </Button>
                )}
              </div>
            )}
            {hubspotFormData && (
              <HubspotForm
                {...hubspotFormData}
                redirectUrl={formRedirectUrl}
              />
            )}
            {children && children}
          </div>
        </div>
      </div>
    </div>
  )
}

export const PrefooterFragment = graphql`
  fragment PrefooterFragment on ContentfulPrefooter {
    bodyText {
      bodyText
    }
    button1Label
    button1Url
    button2Label
    button2Url
    hubspotFormData {
      ...HubspotFormFragment
    }
    formRedirectUrl
    title
  }
`

export default Prefooter
